import { tw } from "@careinfo/tailwind";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import APIS from "../../../constant/Endpoint";
import useGetHook from "../../../customHooks/useGetHook";
import { postApiData } from "../../../helpers/AxiosInstance";
import { getKey, setKey } from "../../../helpers/sessionKey";
import Group from "../../../Icons/GropIcon";
import LeftIcon from "../../../Icons/LeftIcon";
import MenuIcon from "../../../Icons/MenuIcon";
import UserIcon from "../../../Icons/UserIcon";
import Footer from "./Footer";
import Message from "./Message";

const Inbox = ({ group, setOpenInbox }) => {
  const queryClient = useQueryClient();

  const { data: messageHistory, isLoading } = useGetHook({
    queryKey: ["message", `message-${group?.id}`],
    params: { group: group?.id },
    url: `${APIS.MESSAGE}`,
  });

  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const socketRef = useRef(null);
  const endOfMessagesRef = useRef(null);

  const reconnectWebSocket = (newToken) => {
    const ws = new WebSocket(
      `wss://prodenvservice.careinfosolutions.com/chatservice/api/ws/chat/${
        group?.id
      }?token=${encodeURIComponent(newToken)}`
    );

    ws.onmessage = (event) => {
      const incomingMessage = JSON.parse(event.data);
      if (incomingMessage) {
        setMessages((prev) => [...prev, incomingMessage]);
      }
      queryClient.invalidateQueries({
        queryKey: ["group"],
        refetchType: "active",
      });
    };

    ws.onclose = onCloseHandler;
    socketRef.current = ws;
  };

  const refreshAccessToken = async () => {
    const url = APIS.REFRESH_TOKEN;
    const formData = {
      refresh: getKey("refresh-token"),
    };
    try {
      const res = await postApiData({ url, formData, setError: () => {} });
      const newAccessToken = res?.data?.access;
      setKey("token", newAccessToken);
      return newAccessToken;
    } catch (error) {
      console.error("Error refreshing access token:", error);
      throw error;
    }
  };

  const onCloseHandler = async (event) => {
    if (event.code === 10006) {
      console.warn("WebSocket closed due to token expiration.");
      const newToken = await refreshAccessToken();
      if (newToken) reconnectWebSocket(newToken);
    } else {
      console.log("WebSocket closed:", event);
    }
  };

  useEffect(() => {
    const token = getKey("token");
    reconnectWebSocket(token);
    return () => {
      socketRef.current?.close();
    };
  }, [group]);

  useEffect(() => {
    if (messageHistory) {
      setMessages(messageHistory);
    }
  }, [messageHistory]);

  const handleSend = (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;
    try {
      socketRef.current?.send(inputValue);
    } catch (err) {}
    setInputValue("");
  };

  useEffect(() => {
    if (endOfMessagesRef.current) {
      endOfMessagesRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height =
        textareaRef.current.scrollHeight + "px";
    }
  }, [inputValue]);
  const user = getKey("user");
  const userColors = useRef({});
  const getUserColor = (userId) => {
    if (!userColors.current[userId]) {
      const hue = Math.floor(Math.random() * 360);
      userColors.current[userId] = `hsl(${hue}, 70%, 80%)`;
    }
    return userColors.current[userId];
  };

  return (
    <>
      <header
        className={tw`flex items-center bg-white p-2 mb-auto justify-between`}
      >
        <div className={tw`flex items-center gap-x-2`}>
          <button
            type="button"
            className={tw`focus:outline-none`}
            onClick={(e) => {
              e.stopPropagation();
              setOpenInbox({ open: false, id: null, slug: null });
            }}
          >
            <LeftIcon />
          </button>
          {group?.is_private ? (
            <UserIcon width={40} height={40} />
          ) : (
            <Group width={40} height={40} />
          )}
          <h4 className={tw`font-semibold`}>{group?.name}</h4>
        </div>
        <MenuIcon />
      </header>

      <div className={tw`w-full h-full p-2 space-y-2 overflow-auto`}>
        {isLoading
          ? Array.from({ length: 10 }).map((_, idx) => {
              const isMine = idx % 2 === 0;
              const widthClass = ["w-1/3", "w-1/2", "w-2/3"][idx % 3];

              return (
                <div key={idx} className={tw`flex`}>
                  <div
                    className={tw`
            ${isMine ? "ml-auto" : "mr-auto"} 
            ${widthClass} 
            bg-gray-200 
            h-8
            rounded-md 
            p-2 
            my-1 
            animate-pulse
          `}
                  ></div>
                </div>
              );
            })
          : messages?.map((msg, index) => {
              const isMine = msg?.user?.id === user || msg?.user === user;
              return (
                <div key={msg?.user?.id || msg?.user} className={tw`flex`}>
                  <div
                    className={tw`${
                      isMine ? "ml-auto bg-[#D9FED3]" : "mr-auto bg-white"
                    } w-fit p-2 rounded-md relative`}
                  >
                    {!isMine && !group?.is_private && (
                      <p
                        className={tw`-mt-2 text-xs font-semibold`}
                        style={{
                          color: getUserColor(msg?.user?.id || msg?.user),
                        }}
                      >
                        {msg?.user?.name}
                      </p>
                    )}
                    <Message message={msg?.message} />
                    {!isMine && (
                      <div className={tw`absolute top-1 -left-2`}>
                        <svg
                          width="9"
                          height="10"
                          viewBox="0 0 9 10"
                          fill="none"
                        >
                          <path
                            d="M-2.18557e-07 5L9 0.669874L9 9.33013L-2.18557e-07 5Z"
                            fill="#FEFEFE"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
        <div ref={endOfMessagesRef} />
      </div>
      <Footer
        handleSend={handleSend}
        inputValue={inputValue}
        setInputValue={setInputValue}
        textareaRef={textareaRef}
        groupId={group?.id}
      />
    </>
  );
};

export default Inbox;
